import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: number;
  trend?: number;
  format?: 'currency' | 'number' | 'percentage';
  className?: string;
  invertTrend?: boolean; // For cost metrics where decrease = good
  sublabel?: string; // Additional info displayed below main value
}

export const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  trend, 
  format = 'currency',
  className = '',
  invertTrend = false,
  sublabel
}) => {
  const formatValue = (val: number) => {
    if (format === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(val);
    }
    if (format === 'percentage') {
      return `${val.toFixed(1)}%`;
    }
    return val.toLocaleString();
  };

  const getTrendIcon = () => {
    if (trend === undefined) return null;
    
    // For cost metrics (invertTrend = true), decrease is good, increase is bad
    const isPositiveTrend = invertTrend ? trend < 0 : trend > 0;
    const isNegativeTrend = invertTrend ? trend > 0 : trend < 0;
    
    if (trend > 0) {
      return <TrendingUp className={`w-4 h-4 ${isPositiveTrend ? 'text-green-500' : 'text-red-500'}`} />;
    }
    if (trend < 0) {
      return <TrendingDown className={`w-4 h-4 ${isPositiveTrend ? 'text-green-500' : 'text-red-500'}`} />;
    }
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getTrendColor = () => {
    if (trend === undefined) return 'text-gray-900';
    
    // For cost metrics (invertTrend = true), decrease is good, increase is bad
    const isPositiveTrend = invertTrend ? trend < 0 : trend > 0;
    const isNegativeTrend = invertTrend ? trend > 0 : trend < 0;
    
    if (isPositiveTrend) return 'text-green-600';
    if (isNegativeTrend) return 'text-red-600';
    return 'text-gray-600';
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        {getTrendIcon()}
      </div>
      <div className={`text-xl sm:text-2xl font-bold ${getTrendColor()}`}>
        {formatValue(value)}
      </div>
      {sublabel && (
        <div className="text-sm text-gray-600 mt-1">
          {sublabel}
        </div>
      )}
      {trend !== undefined && (
        <div className="text-xs text-gray-500 mt-1">
          {trend > 0 ? '+' : ''}{trend.toFixed(1)}% vs prev period
        </div>
      )}
    </div>
  );
};